import * as Sentry from "@sentry/react";
import { scrubTelemetryValue } from "./scrub.js";

/** Catálogo FASE-00-T12 — nomes tipados; módulos futuros usam os mesmos. */
export type ProductEventName =
  | "session.started"
  | "mfa.enrollment_started"
  | "mfa.enrollment_completed"
  | "pwa.install_prompted"
  | "pwa.installed"
  | "dashboard.viewed"
  | "payslip.listed"
  | "payslip.viewed"
  | "payslip.downloaded"
  | "vacation.balance_viewed"
  | "vacation.request_started"
  | "vacation.request_submitted"
  | "vacation.request_abandoned"
  | "vacation.approved"
  | "vacation.rejected"
  | "notification.sent"
  | "notification.delivered"
  | "notification.opened"
  | "request.created"
  | "request.resolved"
  | "sync.completed"
  | "sync.failed"
  | "stale_data.blocked_action"
  | "sensitive_data.revealed"
  | "export.requested"
  | "telemetry.debug_error";

export type ProductEventContext = {
  tenantId?: string;
  userId?: string;
  role?: string;
};

type Envelope = {
  event: ProductEventName;
  tenant_id: string;
  user_id: string;
  role: string;
  timestamp: string;
  platform: "web" | "pwa";
  trace_id: string;
  props?: Record<string, unknown>;
};

const recent: Envelope[] = [];
let ctx: ProductEventContext = {};
let sessionTraceId: string | undefined;

export function setProductEventContext(next: ProductEventContext | null): void {
  if (next === null) {
    ctx = {};
    sessionTraceId = undefined;
    return;
  }
  if (next.userId !== undefined && next.userId !== ctx.userId) {
    sessionTraceId = undefined;
  }
  ctx = { ...ctx, ...next };
}

function currentTraceId(): string {
  const fromSentry = Sentry.getActiveSpan()?.spanContext().traceId;
  if (fromSentry) return fromSentry;
  if (!sessionTraceId) {
    sessionTraceId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID().replace(/-/g, "")
        : `t_${Date.now().toString(16)}`;
  }
  return sessionTraceId;
}

function isStandalonePwa(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigator &&
      Boolean((navigator as Navigator & { standalone?: boolean }).standalone))
  );
}

/** Pseudônimo estável (não reversível no sink). */
export function pseudonymizeUserId(raw: string): string {
  let h = 2166136261;
  for (let i = 0; i < raw.length; i++) {
    h ^= raw.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return `u_${(h >>> 0).toString(16)}`;
}

export function getRecentProductEvents(): readonly Envelope[] {
  return recent;
}

export function clearRecentProductEvents(): void {
  recent.length = 0;
}

export function track(event: ProductEventName, props?: Record<string, unknown>): void {
  const envelope: Envelope = {
    event,
    tenant_id: ctx.tenantId ?? "unknown",
    user_id: ctx.userId ? pseudonymizeUserId(ctx.userId) : "anonymous",
    role: ctx.role ?? "unknown",
    timestamp: new Date().toISOString(),
    platform: isStandalonePwa() ? "pwa" : "web",
    trace_id: currentTraceId(),
    ...(props ? { props: scrubTelemetryValue(props) } : {}),
  };

  recent.push(envelope);
  if (recent.length > 50) recent.shift();

  if (import.meta.env.DEV || !import.meta.env.VITE_SENTRY_DSN) {
    console.info("[product-event]", envelope);
  }

  Sentry.addBreadcrumb({
    category: "product",
    message: event,
    level: "info",
    data: scrubTelemetryValue({
      tenant_id: envelope.tenant_id,
      user_id: envelope.user_id,
      role: envelope.role,
      platform: envelope.platform,
      trace_id: envelope.trace_id,
      ...envelope.props,
    }),
  });

  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.captureMessage(`product.${event}`, {
      level: "info",
      tags: {
        product_event: event,
        platform: envelope.platform,
        role: envelope.role,
      },
      extra: scrubTelemetryValue({
        tenant_id: envelope.tenant_id,
        user_id: envelope.user_id,
        trace_id: envelope.trace_id,
        props: envelope.props,
      }),
    });
  }
}
